/** @jsx jsx */
import {
  Box,
  Flex,
  FullScreenModalProps,
  Modal,
  ModalContainer,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalHeading,
  SearchIcon,
  TextInput,
} from "@eworkit/iris";
import { FC, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { jsx } from "theme-ui";

import { InfinityList } from "@/components/InfinityList";
import { UserContext } from "@/components/User";
import { fetchConsultantProfileManagerList, IConsultantProfile } from "@/data/consultant-profiles";
import { ApplyManagersListCard } from "@/views/job-applications/apply/_details/ApplyManagersListCard";

interface ApplyContactPersonModalProps extends FullScreenModalProps {
  setContactPerson: (value: IConsultantProfile) => void;
}

export const ApplyContactPersonModal: FC<ApplyContactPersonModalProps> = ({ setContactPerson, ...modalProps }) => {
  const { t } = useTranslation("session");

  const [query, setQuery] = useState("");
  const scrollContainer = useRef(null);

  const { contextCompany } = useContext(UserContext);
  const companyId = contextCompany?.id;
  const header = t("JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.APPLICATION_DETAILS.SELECT_CONTACT");

  const inputRef = useRef<HTMLInputElement>(null);
  const handleChange = useCallback(
    (value: IConsultantProfile) => {
      setContactPerson(value);
      modalProps.dialogState.hide();
    },
    [setContactPerson, modalProps],
  );

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [modalProps.dialogState]);

  return (
    <Modal aria-label={header} {...modalProps}>
      <ModalContainer sx={{ height: ["100vh", null, 500] }}>
        <ModalHeader
          onCloseClick={modalProps.dialogState.hide}
          closeButtonAriaLabel={t("COMPONENTS.MODAL.CLOSE")}
          headerPlaceholder={<ModalHeading aria-label={header}>{header}</ModalHeading>}
          subheaderPlaceholder={
            <Box mt="sm">
              <TextInput
                ref={inputRef}
                size="xl"
                icon={<SearchIcon />}
                placeholder={t("JOB_REQUESTS.APPLY.WIZARD.SEARCH_MANAGER")}
                value={query}
                onChange={setQuery}
              />
            </Box>
          }
        />
        <ModalContent>
          <Flex flexDirection="column" gap="sm">
            <Flex flexDirection="column" ref={scrollContainer}>
              <InfinityList
                renderRecord={cp => (
                  <Box key={cp.id} mb={[null, null, "xs"]} sx={{ "&:last-child": { mb: 0 } }}>
                    <ApplyManagersListCard key={cp.id} profile={cp} onChange={handleChange} />
                  </Box>
                )}
                fetchFunction={fetchConsultantProfileManagerList}
                fetchParams={{ size: 20, companyId, query, queryType: "FULL" }}
                deps={[companyId, query]}
                scrollContainer={scrollContainer}
                noResultsHeader={t("JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.NO_APPLICATION_CONTACT")}
                noResultsText={t("JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.NO_APPLICATION_CONTACT_TEXT")}
              />
            </Flex>
          </Flex>
        </ModalContent>
        <ModalFooter sx={{ "&[data-component='dialog-footer']": { p: "xs" } }} />
      </ModalContainer>
    </Modal>
  );
};
